// What the views read: a project's deal and vendor, and its asks given the
// intake's routing of each need and the answers received.

import { DEALS, PROJECTS, VENDORS } from "./fixtures";
import type { UsageDemoState } from "./store";
import type { Ask, Fulfilment, UsageDeal, UsageProject, UsageVendor } from "./types";

export const projectOf = (id: string): UsageProject | undefined => PROJECTS.find((p) => p.id === id);
export const dealOf = (project: UsageProject): UsageDeal => DEALS.find((d) => d.id === project.dealId)!;
export const vendorOf = (project: UsageProject): UsageVendor => VENDORS.find((v) => v.id === project.vendorId)!;

/** The project whose need this is. */
export const projectOfNeed = (needId: string): UsageProject | undefined =>
    PROJECTS.find((p) => p.needs.some((n) => n.id === needId));

/**
 * Every need with the route the intake gave it, in the needs' order, and the
 * answer once given. Open asks wait on a person or on InfinityLoop; the
 * others were answered or skipped on the intake.
 */
export const asksOf = (project: UsageProject, state: UsageDemoState): Ask[] => {
    const intake = state.intakes[project.id];
    if (!intake) return [];
    return project.needs.flatMap((need) => {
        const how: Fulfilment | undefined = intake.fulfilments[need.id];
        return how ? [{ need, how, answer: state.answers[need.id] }] : [];
    });
};

/** Still waiting on the person it was handed to. */
export const isOpen = (ask: Ask): boolean => ask.how.mode === "delegate" && ask.answer === undefined;

/** The value an ask settled on: the intake's answer, or the delegate's. */
export const settledValue = (ask: Ask): number | string | undefined =>
    ask.how.mode === "answer" ? ask.how.value : ask.answer?.value;

export const deliverableLine = (project: UsageProject, state: UsageDemoState): string => {
    const counts = asksOf(project, state).filter((a) => a.need.answer.kind === "count" && a.how.mode !== "waive");
    const confirmed = counts.reduce((sum, a) => sum + Number(settledValue(a) ?? 0), 0);
    const pending = counts
        .filter((a) => settledValue(a) === undefined)
        .reduce((sum, a) => sum + (a.need.answer.kind === "count" ? a.need.answer.total : 0), 0);
    return project.deliverable.progress(confirmed, pending);
};
